import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type GuestGroup,
  type GuestGroupDetail,
  type GroupMembersResult,
  type GuestBulkResult,
  type CsvImportResult,
} from '../api/client';

// ─── Query Keys ──────────────────────────────────────────────────────────────

const GROUPS_KEY = ['guestGroups'] as const;
const groupDetailKey = (groupId: string) => ['guestGroup', groupId] as const;

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useGuestGroups() {
  return useQuery<GuestGroup[]>({
    queryKey: GROUPS_KEY,
    queryFn: async () => {
      const res = await api.guestGroups.list();
      return res.groups;
    },
  });
}

export function useGuestGroup(groupId: string | undefined) {
  return useQuery<GuestGroupDetail>({
    queryKey: groupDetailKey(groupId!),
    queryFn: () => api.guestGroups.get(groupId!),
    enabled: !!groupId,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.guestGroups.create(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => api.guestGroups.delete(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    },
  });
}

export function useAddGroupMembers() {
  const queryClient = useQueryClient();
  return useMutation<
    GroupMembersResult,
    Error,
    { groupId: string; members: Array<{ email: string; name: string; notes?: string }> }
  >({
    mutationFn: (data) => api.guestGroups.addMembers(data.groupId, data.members),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: groupDetailKey(variables.groupId) });
      queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { groupId: string; memberId: string }>({
    mutationFn: (data) => api.guestGroups.removeMember(data.groupId, data.memberId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: groupDetailKey(variables.groupId) });
      queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    },
  });
}

export function useImportGroupToEvent() {
  const queryClient = useQueryClient();
  return useMutation<GuestBulkResult, Error, { eventId: string; groupId: string }>({
    mutationFn: (data) => api.guestGroups.importToEvent(data.eventId, data.groupId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['guests', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] });
    },
  });
}

export function useCsvImport() {
  const queryClient = useQueryClient();
  return useMutation<CsvImportResult, Error, { eventId: string; csvContent: string }>({
    mutationFn: (data) => api.csvImport.import(data.eventId, data.csvContent),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['guests', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', variables.eventId] });
    },
  });
}
